import { useApplicationData } from '@provider';
import {
  INSTALLATION_OF_PURCHASE,
  PAYMENT_DELAY_DAYS,
  START_REPAYMENTS,
  instantApprovalProducts,
  isJointApplicant,
} from '@utils';
import { getInitialSteps, getStatusSteps, getStepIndexByKey } from './helpers';
import { ApplicationSubmissionStatus, StatusStep, Steps } from './types';

export const useGetStatusSteps = (status: ApplicationSubmissionStatus): Steps => {
  const {
    Finance: finance,
    HouseholdDetails: householdDetails,
    quote,
  } = useApplicationData(['Finance', 'HouseholdDetails', 'quote']);

  const isJointForm = isJointApplicant();
  const hasJoint = !!householdDetails?.hasJointApplicant;
  const jointName = (hasJoint && householdDetails?.jointApplicant?.firstName) || '';
  const financialProductId = quote?.financialProduct.id ?? '';
  const isCashApplication = !!finance?.payment?.isCashApplication;

  const defaultSteps = [
    {
      title: INSTALLATION_OF_PURCHASE.TITLE,
      description: INSTALLATION_OF_PURCHASE.DESCRIPTION,
      content:
        status === ApplicationSubmissionStatus.APPROVED
          ? INSTALLATION_OF_PURCHASE.APPROVED_DESCRIPTION
          : INSTALLATION_OF_PURCHASE.DESCRIPTION,
      key: StatusStep.INSTALLATION_OF_PURCHASE,
    },
    ...(!isCashApplication
      ? [
          {
            title: START_REPAYMENTS.TITLE,
            description: START_REPAYMENTS.description(PAYMENT_DELAY_DAYS(quote), financialProductId),
            key: StatusStep.START_REPAYMENTS,
          },
        ]
      : []),
  ];

  const initialSteps: Steps = getInitialSteps(isJointForm, hasJoint, status);

  const nextSteps = getStatusSteps({
    isJointForm,
    jointApplicantName: jointName,
    applicationStatus: status,
    financialProductId: quote?.financialProduct.id ?? '',
    isCashApplication,
  });

  return [...initialSteps, ...nextSteps, ...defaultSteps];
};

export const useGetNextStepIndex = (steps: Steps, status?: ApplicationSubmissionStatus): number => {
  const {
    Finance: finance,
    HouseholdDetails: householdDetails,
    quote,
  } = useApplicationData(['Finance', 'HouseholdDetails', 'quote']);

  const isCashApplication = !!finance?.payment?.isCashApplication;
  const hasJoint = householdDetails?.hasJointApplicant;

  const jointStepIndex = getStepIndexByKey(StatusStep.JOINT_APPLICATION, steps);
  if (hasJoint && status !== ApplicationSubmissionStatus.APPROVED && jointStepIndex !== -1) {
    return jointStepIndex;
  }

  if (status !== ApplicationSubmissionStatus.APPROVED) {
    return getStepIndexByKey(StatusStep.APPLICATION_UNDER_REVIEW, steps);
  }

  const nextStep =
    isCashApplication || instantApprovalProducts.includes(quote?.financialProduct.id || '')
      ? StatusStep.INSTALLATION_OF_PURCHASE
      : StatusStep.ACCEPT_LOAN_CONTRACT;

  return getStepIndexByKey(nextStep, steps);
};
